// @givmo/api-client — per-request capabilities.
//
// Cross-cutting request features that ride on the generated client's per-request
// `Options` (which extends `RequestInit`, so `headers`/`signal` pass straight
// through) without needing bespoke generated operations:
//   - Idempotency-Key   (super-admin legal broadcast; payment retry de-dupe)
//   - per-request timeout override (the 10-minute accounting export)
//   - binary-response access (content-disposition filename + x-archive-sha256)
//   - forced-token-refresh hook (mint a fresh ID token before a long download
//     so it can't expire mid-transfer)

import type { FirebaseAuthProvider } from './config';

/**
 * Per-request option fragment carrying an `Idempotency-Key` header. Spread into
 * an SDK call's `options`. The key is the CALLER'S responsibility to generate
 * once per attempt-series and reuse across retries (ports the payment-repo
 * idempotency note); a new key per retry defeats server-side de-dupe.
 *
 * @example sdk.broadcastLegalNotice({ ...idempotencyKey(uuid), body })
 */
export function idempotencyKey(key: string): { headers: Record<string, string> } {
  return { headers: { 'Idempotency-Key': key } };
}

/**
 * Per-request option fragment overriding the timeout. Native fetch has no
 * `timeout` field, so this emits an `AbortSignal.timeout(ms)`. Use for the
 * 10-minute ZIP export where the client default is too short.
 *
 * @example sdk.exportAccounting({ ...requestTimeout(600_000), body })
 */
export function requestTimeout(ms: number): { signal: AbortSignal } {
  return { signal: AbortSignal.timeout(ms) };
}

/** Metadata extracted from a binary download response. */
export interface BinaryDownloadMeta {
  /** Suggested filename from `Content-Disposition`, or `null` if absent. */
  readonly filename: string | null;
  /** Integrity hash from `x-archive-sha256` (ZIP accounting export), or `null`. */
  readonly sha256: string | null;
  /** The `Content-Type`, e.g. `application/zip` / `application/pdf`. */
  readonly contentType: string | null;
}

/**
 * Read binary-download metadata off a raw `Response` (use the `fields`
 * response-style so you have the `response`). Pairs with `parseAs: 'blob'`.
 */
export function readBinaryMeta(response: Response): BinaryDownloadMeta {
  return {
    filename: parseContentDispositionFilename(response.headers.get('content-disposition')),
    sha256: response.headers.get('x-archive-sha256'),
    contentType: response.headers.get('content-type'),
  };
}

/** Parse the `filename` (or RFC 5987 `filename*`) out of a Content-Disposition header. */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  // RFC 5987 `filename*=UTF-8''...` takes precedence (handles non-ASCII).
  const extended = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      return extended[1].trim();
    }
  }
  const basic = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return basic?.[1]?.trim() ?? null;
}

/**
 * Forced-token-refresh hook: mint a FRESH Firebase ID token before a long
 * download so it cannot expire mid-transfer, and return it as an `Authorization`
 * header fragment to spread into the request `options`. The same-origin Bearer
 * interceptor RESPECTS an already-present `Authorization` header (it only mints
 * a token when none is set), so this forced-refresh value wins for that request.
 *
 * @example
 *   const fresh = await forceRefreshAuthHeader(auth);
 *   await sdk.exportAccounting({ ...fresh, ...requestTimeout(600_000), body });
 */
export async function forceRefreshAuthHeader(
  auth: FirebaseAuthProvider,
): Promise<{ headers: Record<string, string> } | null> {
  const token = await auth.getIdToken(true);
  if (!token) return null;
  return { headers: { Authorization: `Bearer ${token}` } };
}
